import {
  Box,
  Divider,
  HStack,
  IconButton,
  Progress,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { FiChevronDown, FiChevronUp, FiX } from 'react-icons/fi'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  selectIsUploadPanelVisible,
  selectUploadItems,
  selectUploadPanelMinimized,
  selectUploadSummary,
} from '@/store/selectors'
import { uploadPanelClosed, uploadPanelMinimizedToggled } from '@/store/slices/uploadsSlice'
import { useAppTranslation } from '@/contexts/LanguageContext'
import UploadPanelRow from './UploadPanelRow'

const MotionBox = motion(Box as any)

/**
 * Floating upload tray, anchored bottom-right like Dropbox's.
 *
 * This is the only surface that shows upload progress — the file table renders
 * real rows only, so nothing appears twice.
 */
export default function UploadPanel() {
  const dispatch = useAppDispatch()
  const { t } = useAppTranslation('files')

  const isVisible = useAppSelector(selectIsUploadPanelVisible)
  const isMinimized = useAppSelector(selectUploadPanelMinimized)
  const items = useAppSelector(selectUploadItems)
  const summary = useAppSelector(selectUploadSummary)

  const panelBg = useColorModeValue('white', 'gray.800')
  const headerBg = useColorModeValue('gray.50', 'gray.900')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300')
  const titleColor = useColorModeValue('gray.800', 'gray.100')
  const subtleColor = useColorModeValue('gray.500', 'gray.400')

  const title = () => {
    if (summary.isActive) {
      return t('upload.uploadingCount', { count: summary.activeCount })
    }

    if (summary.failedCount > 0) {
      return t('upload.failedCount', { count: summary.failedCount })
    }

    return t('upload.completedCount', { count: summary.completedCount })
  }

  const subtitle = () => {
    const parts: string[] = []

    if (summary.conflictCount > 0) {
      parts.push(t('upload.needsAttention', { count: summary.conflictCount }))
    }
    if (summary.skippedCount > 0) {
      parts.push(t('upload.skippedCount', { count: summary.skippedCount }))
    }

    return parts.join(' · ')
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <MotionBox
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          position="fixed"
          bottom={{ base: '10px', md: '20px' }}
          right={{ base: '10px', md: '20px' }}
          // Above the chat widget (1000), which hides while this is on screen.
          zIndex={1400}
          w={{ base: 'calc(100vw - 20px)', md: '380px' }}
          bg={panelBg}
          borderRadius="lg"
          border="1px solid"
          borderColor={borderColor}
          boxShadow="2xl"
          overflow="hidden"
        >
          <HStack
            px={3}
            py={2.5}
            bg={headerBg}
            spacing={2}
            cursor="pointer"
            onClick={() => dispatch(uploadPanelMinimizedToggled())}
          >
            <VStack spacing={0} align="start" flex={1} minW={0}>
              <Text fontSize="sm" fontWeight="semibold" color={titleColor} noOfLines={1}>
                {title()}
              </Text>
              {subtitle() && (
                <Text fontSize="xs" color={subtleColor} noOfLines={1}>
                  {subtitle()}
                </Text>
              )}
            </VStack>

            <IconButton
              aria-label={
                isMinimized ? t('upload.expand') : t('upload.minimize')
              }
              icon={isMinimized ? <FiChevronUp size="16px" /> : <FiChevronDown size="16px" />}
              size="sm"
              variant="ghost"
              onClick={(event) => {
                event.stopPropagation()
                dispatch(uploadPanelMinimizedToggled())
              }}
            />

            {/* Closing is only offered once nothing is still in flight. */}
            {!summary.isActive && (
              <IconButton
                aria-label={t('upload.close')}
                icon={<FiX size="16px" />}
                size="sm"
                variant="ghost"
                onClick={(event) => {
                  event.stopPropagation()
                  dispatch(uploadPanelClosed())
                }}
              />
            )}
          </HStack>

          {summary.isActive && (
            <Progress
              value={summary.progress}
              size="xs"
              colorScheme="blue"
              bg="transparent"
              borderRadius={0}
            />
          )}

          {!isMinimized && (
            <>
              <Divider borderColor={borderColor} />
              <VStack spacing={0} align="stretch" maxH="320px" overflowY="auto">
                {items.map((item) => (
                  <UploadPanelRow key={item.id} item={item} />
                ))}
              </VStack>
            </>
          )}
        </MotionBox>
      )}
    </AnimatePresence>
  )
}
